/* ============================================================================
   TCL Custom JavaScript - Production Theme
   Core functionality for all interactive components
   ============================================================================ */

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Debounce function to limit execution frequency
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 */
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Check if element is in viewport
 * @param {Element} el - Element to check
 */
function isInViewport(el) {
  const rect = el.getBoundingClientRect();
  return (
    rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.bottom >= 0
  );
}

// ============================================================================
// SCROLL ANIMATIONS
// ============================================================================

const ScrollAnimations = {
  init() {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });

    document.querySelectorAll('[class*="tcl-fade-"], [class*="tcl-slide-"]').forEach(el => {
      observer.observe(el);
    });
  }
};

// ============================================================================
// MOBILE MENU
// ============================================================================

const MobileMenu = {
  init() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    const close = document.querySelector('[data-mobile-menu-close]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');

    if (!toggle || !menu) return;

    toggle.addEventListener('click', () => this.open());
    close?.addEventListener('click', () => this.close());
    overlay?.addEventListener('click', () => this.close());

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  },

  open() {
    const menu = document.querySelector('[data-mobile-menu]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    menu?.classList.add('is-open');
    overlay?.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  },

  close() {
    const menu = document.querySelector('[data-mobile-menu]');
    const overlay = document.querySelector('[data-mobile-menu-overlay]');
    menu?.classList.remove('is-open');
    overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }
};

// ============================================================================
// SEARCH OVERLAY
// ============================================================================

const SearchOverlay = {
  init() {
    const toggle = document.querySelector('[data-search-toggle]');
    const overlay = document.querySelector('[data-search-overlay]');
    const input = document.querySelector('[data-search-input]');

    if (!toggle || !overlay) return;

    toggle.addEventListener('click', () => this.open());

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.close();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });
  },

  open() {
    const overlay = document.querySelector('[data-search-overlay]');
    const input = document.querySelector('[data-search-input]');
    overlay?.classList.add('is-open');
    input?.focus();
    document.body.style.overflow = 'hidden';
  },

  close() {
    const overlay = document.querySelector('[data-search-overlay]');
    overlay?.classList.remove('is-open');
    document.body.style.overflow = '';
  }
};

// ============================================================================
// MEGA MENU
// =====================================================================================

const MegaMenu = {
  init() {
    const navItems = document.querySelectorAll('[data-nav-link]');
    navItems.forEach(link => {
      const parent = link.closest('.tcl-header__nav--item');
      const megaMenu = parent?.querySelector('[data-mega-menu]');

      if (megaMenu) {
        link.addEventListener('mouseenter', () => {
          megaMenu.classList.add('is-open');
        });

        parent.addEventListener('mouseleave', () => {
          megaMenu.classList.remove('is-open');
        });
      }
    });
  }
};

// ============================================================================
// HERO SLIDESHOW
// ============================================================================

const HeroSlideshow = {
  init() {
    const slideshows = document.querySelectorAll('[data-hero-slideshow]');
    slideshows.forEach(slideshow => this.setupSlideshow(slideshow));
  },

  setupSlideshow(container) {
    const slides = container.querySelectorAll('[data-slide]');
    const dots = container.querySelectorAll('[data-dot]');
    const prevBtn = container.querySelector('[data-slide-prev]');
    const nextBtn = container.querySelector('[data-slide-next]');

    if (slides.length === 0) return;

    let currentIndex = 0;
    let autoPlayInterval = null;

    const showSlide = (index) => {
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
      });

      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });

      currentIndex = index;
    };

    const nextSlide = () => {
      showSlide((currentIndex + 1) % slides.length);
      resetAutoPlay();
    };

    const prevSlide = () => {
      showSlide((currentIndex - 1 + slides.length) % slides.length);
      resetAutoPlay();
    };

    const startAutoPlay = () => {
      if (slides.length > 1) {
        autoPlayInterval = setInterval(nextSlide, 5000);
      }
    };

    const resetAutoPlay = () => {
      clearInterval(autoPlayInterval);
      startAutoPlay();
    };

    if (prevBtn) prevBtn.addEventListener('click', prevSlide);
    if (nextBtn) nextBtn.addEventListener('click', nextSlide);

    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        showSlide(i);
        resetAutoPlay();
      });
    });

    container.addEventListener('mouseenter', () => clearInterval(autoPlayInterval));
    container.addEventListener('mouseleave', startAutoPlay);

    // Touch support
    let touchStartX = 0;
    container.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    });

    container.addEventListener('touchend', (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      if (touchStartX - touchEndX > 50) nextSlide();
      else if (touchEndX - touchStartX > 50) prevSlide();
    });

    startAutoPlay();
  }
};

// ============================================================================
// ANNOUNCEMENT BAR ROTATION
// ============================================================================

const AnnouncementBar = {
  init() {
    const bars = document.querySelectorAll('[data-announcement-bar]');
    bars.forEach(bar => this.setupBar(bar));
  },

  setupBar(bar) {
    const messages = Array.from(bar.querySelectorAll('[data-announcement-message]'));
    if (messages.length <= 1) return;

    const prevBtn = bar.querySelector('[data-announcement-prev]');
    const nextBtn = bar.querySelector('[data-announcement-next]');
    const messageEl = bar.querySelector('[data-announcement-message]');

    let currentIndex = 0;
    let autoPlayInterval = null;

    const updateMessage = () => {
      messageEl.style.animation = 'none';
      setTimeout(() => {
        messageEl.textContent = messages[currentIndex].textContent;
        messageEl.style.animation = '';
      }, 10);
    };

    const showNext = () => {
      currentIndex = (currentIndex + 1) % messages.length;
      updateMessage();
      resetAutoPlay();
    };

    const showPrev = () => {
      currentIndex = (currentIndex - 1 + messages.length) % messages.length;
      updateMessage();
      resetAutoPlay();
    };

    const startAutoPlay = () => {
      autoPlayInterval = setInterval(showNext, 5000);
    };

    const resetAutoPlay = () => {
      clearInterval(autoPlayInterval);
      startAutoPlay();
    };

    if (prevBtn) prevBtn.addEventListener('click', showPrev);
    if (nextBtn) nextBtn.addEventListener('click', showNext);

    bar.addEventListener('mouseenter', () => clearInterval(autoPlayInterval));
    bar.addEventListener('mouseleave', startAutoPlay);

    startAutoPlay();
  }
};

// ============================================================================
// TRENDING PRODUCTS TABS
// ============================================================================

const TrendingTabs = {
  init() {
    const tabs = document.querySelectorAll('[data-tab]');
    const tabContents = document.querySelectorAll('[data-tab-content]');
    const collectionLinks = document.querySelectorAll('[data-collection-link]');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const tabId = tab.getAttribute('data-tab');

        tabs.forEach(t => t.classList.remove('is-active'));
        tab.classList.add('is-active');

        tabContents.forEach(content => {
          if (content.getAttribute('data-tab-content') === tabId) {
            content.style.display = 'block';
          } else {
            content.style.display = 'none';
          }
        });

        collectionLinks.forEach(link => {
          if (link.getAttribute('data-collection-link') === tabId) {
            link.style.display = 'block';
          } else {
            link.style.display = 'none';
          }
        });
      });
    });
  }
};

// ============================================================================
// CAROUSEL CONTROLS
// ==========================================================

const CarouselControls = {
  init() {
    const prevBtn = document.querySelector('[data-carousel-prev]');
    const nextBtn = document.querySelector('[data-carousel-next]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const carousel = document.querySelector('.tcl-trending-products__carousel-container');
        if (carousel) carousel.scrollBy({ left: -300, behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const carousel = document.querySelector('.tcl-trending-products__carousel-container');
        if (carousel) carousel.scrollBy({ left: 300, behavior: 'smooth' });
      });
    }
  }
};

// ============================================================================
// PRODUCT GALLERY
// ============================================================================

const ProductGallery = {
  init() {
    const galleries = document.querySelectorAll('[data-main-image]');
    galleries.forEach(gallery => this.setupGallery(gallery));
  },

  setupGallery(gallery) {
    const mainImg = gallery.querySelector('[data-main-img]');
    const thumbs = gallery.querySelectorAll('[data-thumb]');
    const modal = gallery.closest('section')?.querySelector('[data-modal]');
    const modalImage = modal?.querySelector('[data-modal-image]');
    const modalClose = modal?.querySelector('[data-modal-close]');

    if (!mainImg) return;

    // Thumbnail click handler
    thumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const image = thumb.querySelector('img');
        if (image) {
          mainImg.src = image.src.replace(/_\d+x\d+/, '_800x800');
          mainImg.alt = image.alt;

          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      });
    });

    // Zoom modal
    if (modal && modalImage) {
      gallery.addEventListener('click', () => {
        modalImage.src = mainImg.src;
        modal.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });

      modalClose?.addEventListener('click', () => {
        modal.classList.remove('is-open');
        document.body.style.overflow = '';
      });

      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          modal.classList.remove('is-open');
          document.body.style.overflow = '';
        }
      });
    }
  }
};

// ============================================================================
// PRODUCT TABS
// ============================================================================

const ProductTabs = {
  init() {
    const allTabs = document.querySelectorAll('[data-tab]');
    const allContents = document.querySelectorAll('[data-tab-content]');

    allTabs.forEach(button => {
      button.addEventListener('click', () => {
        const tabId = button.getAttribute('data-tab');

        allTabs.forEach(b => b.classList.remove('is-active'));
        button.classList.add('is-active');

        allContents.forEach(content => {
          if (content.getAttribute('data-tab-content') === tabId) {
            content.classList.add('is-active');
          } else {
            content.classList.remove('is-active');
          }
        });
      });
    });
  }
};

// ============================================================================
// COMPARISON TABLE TOGGLE
// ============================================================================

const ComparisonTable = {
  init() {
    const toggles = document.querySelectorAll('[data-toggle]');
    const sections = document.querySelectorAll('[data-section]');

    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const sectionName = toggle.getAttribute('data-toggle');

        toggles.forEach(t => t.classList.remove('is-active'));
        toggle.classList.add('is-active');

        sections.forEach(section => {
          if (section.getAttribute('data-section') === sectionName) {
            section.classList.add('is-active');
          } else {
            section.classList.remove('is-active');
          }
        });
      });
    });
  }
};

// ============================================================================
// VARIANT SELECTOR
// ============================================================================

const VariantSelector = {
  init() {
    const options = document.querySelectorAll('[data-option]');
    options.forEach(option => {
      option.addEventListener('click', (e) => {
        e.preventDefault();

        const optionName = option.getAttribute('data-option');

        document.querySelectorAll(`[data-option="${optionName}"]`).forEach(opt => {
          opt.classList.remove('is-active');
        });

        option.classList.add('is-active');
      });
    });
  }
};

// ============================================================================
// NEWSLETTER FORM
// ============================================================================

const Newsletter = {
  init() {
    const forms = document.querySelectorAll('.tcl-newsletter__form');

    forms.forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        const email = form.querySelector('input[name="contact[email]"]')?.value;
        const consent = form.querySelector('input[name="consent"]')?.checked;

        if (!email || !consent) {
          alert('Please enter a valid email and agree to receive communications.');
          return;
        }

        try {
          const formData = new FormData(form);
          const response = await fetch('/contact', {
            method: 'POST',
            body: formData
          });

          if (response.ok) {
            form.innerHTML = '<p style="font-size: var(--tcl-size-lg); color: var(--tcl-primary-red); font-weight: 500;">Thank you for subscribing!</p>';
          }
        } catch (error) {
          console.error('Newsletter signup error:', error);
          alert('There was an error subscribing. Please try again.');
        }
      });
    });
  }
};

// ============================================================================
// INITIALIZATION
// ============================================================================

document.addEventListener('DOMContentLoaded', function() {
  // Initialize all components
  ScrollAnimations.init();
  MobileMenu.init();
  SearchOverlay.init();
  MegaMenu.init();
  HeroSlideshow.init();
  AnnouncementBar.init();
  TrendingTabs.init();
  CarouselControls.init();
  ProductGallery.init();
  ProductTabs.init();
  ComparisonTable.init();
  VariantSelector.init();
  Newsletter.init();

  console.log('TCL Theme initialized successfully');
});

// ============================================================================
// UTILITIES FOR DEVELOPMENT
// ============================================================================

window.TCL = {
  version: '1.0.0',
  debug: false,

  log(message, data) {
    if (this.debug) {
      console.log(`[TCL] ${message}`, data || '');
    }
  }
};
